/**
 * Custom event component for react-big-calendar
 */

import { Mail } from 'lucide-react'
import { cn } from '@/utils/misc'
import { isCancelledBackground, isCancelledOnly, getContrastColor } from '@/lib/constants'
import type { AppointmentType, CalendarEvent } from '@/types/calendar'

interface CalendarEventProps {
	event: CalendarEvent
}

export function CalendarEventComponent({ event }: CalendarEventProps) {
	const { resource } = event
	const background = resource.background || '#4285f4'
	const isCancelled = isCancelledBackground(background)
	const textColor = getContrastColor(background)

	// Calculate duration in minutes
	const durationMinutes = resource.duration

	// Format time display based on duration
	const isShortEvent = durationMinutes <= 30

	// SMS indicator - show if smsRequired has a value (not null/undefined)
	// Red if smsRequired is 'yes', white otherwise
	const smsCount = resource.smsCount ?? 0
	const showSms = resource.smsRequired != null
	const smsColor = resource.smsRequired === 'yes' ? '#FF2E00' : 'white'

	return (
		<div
			className={cn('calendar-event', isCancelled && 'calendar-event--cancelled')}
			style={{ color: textColor }}
		>
			{isShortEvent ? (
				// Short events (≤30min): "[sms] title, startTime" on one line
				<span className="calendar-event__title">
					{showSms && (
						<span className="calendar-event__sms-indicator" style={{ color: smsColor }}>
							{smsCount > 1 && <span>{smsCount}</span>}
							<Mail size={12} />
						</span>
					)}
					{event.title}<span className="calendar-event__time">, {resource.startTime}</span>
				</span>
			) : (
				// Long events (>30min): title on first line, time range on second
				<>
					<span className="calendar-event__title">
						{showSms && (
							<span className="calendar-event__sms-indicator" style={{ color: smsColor }}>
								{smsCount > 1 && <span>{smsCount}</span>}
								<Mail size={12} />
							</span>
						)}
						{event.title}
					</span>
					<span className="calendar-event__time-range">
						{resource.startTime} - {resource.endTime}
					</span>
				</>
			)}
		</div>
	)
}

/**
 * Event style getter factory for react-big-calendar.
 * Events from a calendar type other than the current one are rendered with
 * reduced opacity (ghost preview from the other calendar).
 */
export function createEventStyleGetter(calendarType: AppointmentType) {
	return function getEventStyle(event: CalendarEvent) {
		const background = event.resource.background || '#4285f4'
		const isGhost = event.resource.type !== calendarType

		return {
			style: {
				backgroundColor: background,
				borderRadius: '4px',
				// Gray cascade on top; no-shows lie beside active events (see dayLayoutAlgorithm)
				zIndex: isCancelledOnly(background) ? 10 : 1,
				opacity: isGhost ? 0.4 : 1,
			},
		}
	}
}

/**
 * Slot style getter for react-big-calendar
 */
export function getSlotStyle(date: Date) {
	const now = new Date()
	const isToday =
		date.getDate() === now.getDate() &&
		date.getMonth() === now.getMonth() &&
		date.getFullYear() === now.getFullYear()

	if (isToday) {
		return {
			style: {
				backgroundColor: '#eff6ff',
			},
		}
	}
	return {}
}
